/**
 * Connects selection models to the dom.
 *
 * @mdoule app/views/SelectionViewAdapter
 */
import _ from 'lodash';
import BetDropdownViewAdapter from '../views/BetDropdownViewAdapter';
import BetSlipAdapter from '../components/BetSlipAdapter';
import Date from '../components/Date';
import docCookies from 'doc-cookies';
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import modelComposer from '../components/ModelComposer';
import QuickBets from '../components/QuickBets';
import UrlArgsProvider from '../components/UrlArgsProvider';
import constant from '../constants/config';
import { trackLink } from '../libs/trackLink';
import Translator from '../components/Translator';

/**
 * Limit on max selections that can be added to betslip
 * see: https://williamhill.jira.com/browse/SBK-628
 *
 * @type {Number}
 */
var maxBetslipSelections = 30;

/**
 * Selection IDs added to betslip
 *
 * @type {Array}
 */
var highlightedSelections = [];

/**
 * Selection that were clicked before betslip is initiated
 * (before firing MessageBusTopics.BETSLIP_ADAPTER_GET_BETS event for 1st time)
 *
 * Queue is resolved on MessageBusTopics.BETSLIP_ADAPTER_GET_BETS event.
 *
 * @type {Array} Array of jQuery selection nodes
 */
var betslipClickQueue = [];

/**
 * Flag determining if betslip is fully loaded and can handle selections.
 * If not queueing mechanism is being used.
 *
 * @type {Boolean}
 */
var initialBetslipLoaded = false;

/**
 * Filter element in queue by removing items that are already in  betslip
 *
 * @param {jQuery[]} queue Array of selection nodes
 * @param {String[]} added Array of selections' ids from betslip
 *
 * @returns {jQuery[]}
 *
 * @private
 */
var filterAlreadyAdded = function filterAlreadyAdded(queue, added) {

    /**
     * Iterator to filter queue
     *
     * @param {jQuery} selectionNode
     *
     * @returns {Boolean}
     */
    var filterIterator = function filterIterator(selectionNode) {

        var selectionId = selectionNode.data('entityid');

        if (selectionId) {

            return !_.includes(added, selectionId.replace(/OB_OU/, ''));
        }

        return false;
    };

    return _.filter(queue, filterIterator);
};

/**
 * Resolve queued selection object
 *
 * @param {jQuery} selectionView Selection node
 *
 * @returns {void}
 *
 * @private
 */
var resolveQueuedSelection = function resolveQueuedSelection(selectionView) {

    bindEventsRunActions.handleAnimation(selectionView);

    bindEventsRunActions.sendToBetSlip(
        bindEventsRunActions.modelFromView(selectionView),
        true
    );
};

/**
 * Resolve queue of selections which were waiting for betslip to ba available.
 *
 * @param {String} topic
 * @param {String[]} selections Selections that are currently in betslip
 *
 * @returns {void}
 *
 * @private
 */
var initialBetsSubscribe = function initialBetsSubscribe(topic, selections) {

    initialBetslipLoaded = true;

    if (betslipClickQueue.length === 0) {

        return;
    }

    var newSelection = filterAlreadyAdded(betslipClickQueue, selections);

    betslipClickQueue = [];

    jQuery('.betbutton--loading').removeClass('betbutton--loading');

    _.each(newSelection, resolveQueuedSelection);
};

/**
 * Obtains count of highlightedSelections
 *
 * @returns {Integer}
 *
 * @private
 */
var getHighlightedSelectionsCount = function getHighlightedSelectionsCount() {

    var selections = _.extend([], highlightedSelections);

    _.each(selections, function (selection, index) {

        selections[index] = selection.replace('_SP', '');
    });

    return _.uniq(selections).length;
};

/**
 * Sort selections by fbResult.
 *
 * @param {Object} selection Selection.
 *
 * @returns {Mixed}
 *
 * @private
 */
var sortByFBResult = function (selection) {

    return selection.fbResult;
};

/**
 * Sort selections by price.
 *
 * @param {Object} selection Selection.
 *
 * @returns {Mixed}
 *
 * @private
 */
var sortByPrice = function (selection) {

    return (selection.priceNumerator / selection.priceDenominator);
};

/**
 * Returns selection price type based on selection ID
 * after parsing a JSON string coming from the betslip API,
 * which will maybe be deprecated someday
 *
 * @param  {String} selectionId Selection ID
 *
 * @returns {String|Boolean} Price type or false if API is unavailable
 *
 * @private
 */
var getSelectionPriceType = function (selectionId) {

    var bets = document.sportsbookAPI.betslipAPI.getBets()[1];

    /**
     * Returns selection price if available, defaults to 'L'
     *
     * @param  {Object} selection Selection from betslip object
     *
     * @returns {String} Price type
     *
     * @private
     */
    var getType = function (selection) {

        if (selection) {
            return selection.price.type;
        }
        return 'L';
    };
    if (bets.indexOf('singles') !== -1) {

        var selection = _.find(
            JSON.parse(bets).singles, {
                /*jshint -W106 */
                ev_oc_id: selectionId,
                /*jshint +W106 */
            }
        );

        return getType(selection);
    }

    return false;
};

/**
 * Get selector for Live Prices, handling cast markets
 *
 * @param {String} selectionId Selection ID
 *
 * @returns {String}
 *
 * @private
 */
var livePriceSelector = function (selectionId) {

    if (selectionId.indexOf(' ') !== -1) {

        return selectionId.replace(' ', '|');
    }

    return 'OB_OU' + selectionId.replace('L', '');
};

/**
 * Returns selection selector based on price type
 *
 * @param  {String} selectionId Selection ID
 * @param  {String} priceType   Selection Price Type
 *
 * @return {String}             Selection selector
 *
 * @private
 */
var getSelector = function (selectionId, priceType) {

    if (priceType === 'S') {

        return 'OB_OU' + selectionId + '_SP';
    }

    return livePriceSelector(selectionId);
};

/**
 * Handles classes for banner selections
 *
 * @param  {String}  selectionId Selection ID
 * @param  {Boolean} highlight   Highlight flag
 *
 * @return {void}
 *
 * @private
 */
var handleBannerHighlight = function (selectionId, highlight) {

    /**
     * Returns the root element
     *
     * @returns {Element}
     *
     * @private
     */
    var getRootElement = function () {

        return document.getElementById('contentarea');
    };

    var bannerSelectionView = jQuery(
        getRootElement().querySelectorAll('.banners-container [data-entityid="OB_OU' + selectionId + '"]')
    );

    if (bannerSelectionView.length !== 0) {
        bannerSelectionView.toggleClass('active -selectionadded', highlight);
    }
};

/**
 * Handles classes for enhanced offers selections
 *
 * @param  {String} selectionId Selection ID
 * @param  {Boolean} highlight  Highlight flag
 *
 * @return {void}
 *
 * @private
 */
var handleEnhancedOffersHighlight = function (selectionId, highlight) {

    var enhancedSelectionView = jQuery('#enhanced').find('[data-entityid="OB_OU' + selectionId + '"]');

    if (enhancedSelectionView.length !== 0) {

        enhancedSelectionView.toggleClass('active -selectionadded', highlight);
    }
};

/**
 * Switch price type from S to L and from L to S
 *
 * @param {String} priceType Price Type (L/S)
 *
 * @returns {String}
 *
 * @private
 */
var switchPriceType = function (priceType) {

    return priceType === 'S' ? 'L' : 'S';
};

/**
 * Handles classes for regular selections
 *
 * @param  {String} selectionId Selection ID
 * @param  {Boolean} highlight  Highlight flag
 * @param  {String} priceType Price type
 *
 * @return {void}
 *
 * @private
 */
var handleSelectionHighlight = function (selectionId, highlight, priceType) {

    if (getSelector(selectionId, priceType).indexOf('|') === -1) {

        jQuery('button[id=\'' + getSelector(selectionId, priceType) + '\']')
            .toggleClass('active', highlight);

        jQuery('button[id=\'' + getSelector(selectionId, switchPriceType(priceType)) + '\']')
            .removeClass('active', highlight);
    } else {

        jQuery('.oddsbutton[data-entityid="' + getSelector(selectionId, priceType) + '"]')
            .toggleClass('active', highlight);
    }
};

/**
 * Connect special handlers on betslip update
 *
 * @param   {Number}  selectionId   Selection ID
 * @param   {Boolean} highlight     Whether or not to highlight the selection.
 *
 * @returns {void}
 *
 * @private
 */
var handleSpecialSelectionHighlight = function handleSpecialSelectionHighlight(selectionId, highlight) {

    handleBannerHighlight(selectionId, highlight);
    handleEnhancedOffersHighlight(selectionId, highlight);
};

/**
 * Betslip Update event handler, used for
 * updating the view states (active/inactive).
 *
 * @param {Number} selectionId Selection ID.
 * @param {Boolean} highlight Whether or not to highlight the selection.
 *
 * @returns {void}
 *
 * @private
 */
var onBetSlipUpdate = function (selectionId, highlight) {

    //TODO: do work to determine actual priceType

    var priceType = 'L'; //getSelectionPriceType(selectionId);

    if (window.whStatic.config.enableSelectionViewAdapterSelectionPriceType) {
        priceType = getSelectionPriceType(selectionId);
    }

    if (priceType === false) {
        return setTimeout(function () {
            onBetSlipUpdate(selectionId, highlight);
        }, 300);
    }

    manageHighlightModel(getSelector(selectionId, priceType), highlight);
    handleSelectionHighlight(selectionId, highlight, priceType);

    handleSpecialSelectionHighlight(selectionId, highlight);
};

/**
 * Add selection to highlightedSelections array
 *
 * @param   {String} selectionId
 *
 * @returns {void}
 *
 * @private
 */
var addSelectionToHighlight = function (selectionId) {

    if (_.indexOf(highlightedSelections, selectionId) === -1) {
        highlightedSelections.push(selectionId);
    }
};

/**
 * Manage highlightedSelections array
 *
 * @param   {String} selectionId
 * @param   {Boolean} highlight
 *
 * @returns {void}
 *
 * @private
 */
var manageHighlightModel = function (selectionId, highlight) {

    if (highlight === true) {
        addSelectionToHighlight(selectionId);
    } else {
        highlightedSelections.splice(_.indexOf(highlightedSelections, selectionId), 1);
    }
};

/**
 * Get Patterns based on sort code
 *
 * @param   {String} sortCode
 *
 * @returns {Object/Boolean}
 *
 * @private
 */
var getPatternsBySortCode = function (sortCode) {

    // based on type (hh win/win ) and fbresults of each selection
    var patterns = {
        'HH': { // win/win
            'H': 0,
            'A': 1,
        },
        'OE': { // odd/even
            '1': 0,
            '2': 1,
        },
        'WH': { //
            'H': 0,
            'A': 1,
        },
        'HT': { // half time
            'H': 0,
            'D': 1,
            'A': 2,
        },
        'H1': { // first half
            'H': 0,
            'D': 1,
            'A': 2,
        },
        'LG': { // last to score
            'H': 0,
            'D': 1,
            'A': 2,
        },
        'SF': { // First to score
            'H': 0,
            'A': 1,
        },
        'HL': { // Over / under
            'L': 0,
            'H': 1,
        },
        'DN': { // Draw no bet
            'H': 0,
            'A': 1,
        },
        'H2': { // second half
            'H': 0,
            'D': 1,
            'A': 2,
        },
        'DC': { // double chance
            '1': 0,
            '2': 1,
            '3': 2,
        },
        'MH': { // double chance
            'H': 0,
            'L': 1,
            'A': 2,
        },
        'MR': { // double chance
            'H': 0,
            'D': 1,
            'A': 2,
        },
    };

    return (patterns.hasOwnProperty(sortCode)) ? patterns[sortCode] : false;
};

/**
 * Sorting Functions
 *
 * @param {Object} selections Selections
 * @param {Object} marketData
 *
 * @returns {Object}            Sorted selections
 */
var sortInColumns = function (selections, marketData) {

    /**
     * Order selections by FBResult
     *
     * @param   {Object} selections
     *
     * @returns {Array}
     *
     * @private
     */
    var orderSelectionsByFBResult = function () {
        return _.sortBy(selections, sortByFBResult);
    };

    /**
     * Order selections by Price
     *
     * @param   {Object} selections
     *
     * @returns {Array}
     *
     * @private
     */
    var orderSelectionsByPrice = function () {
        return _.sortBy(selections, sortByPrice);
    };

    /**
     * Sorting methods
     *
     * @type {Object}
     */
    var sorting = {
        /**
         * for cases with max 3 selections, so only one row
         *
         * @returns {Array}
         *
         * @private
         */
        genericSort: function () {

            var pattern = getPatternsBySortCode(marketData.marketSort);

            /**
             * Map Results
             *
             * @returns {Array}
             *
             * @private
             */
            var mapResult = function () {

                // Object used first as some patterns user an int as index which
                // fucks up arrays and leaves undefined indexes if they start at one etc
                var result = {},
                    resultsArr = [];

                /**
                 * Define columns
                 *
                 * @param   {String} id
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var columnDefine = function (id) {

                    /**
                     * Push selections to column
                     *
                     * @param   {String} sid
                     * @param   {Object} selection
                     *
                     * @returns {void}
                     *
                     * @private
                     */
                    var pushSelectionToColumn = function (sid, selection) {

                        if (selection.fbResult === id) {
                            result[id].push(selection);
                        }
                    };

                    result[id] = [];

                    // loop through selections and push to corresponding column
                    jQuery.each(selections, pushSelectionToColumn);

                    // Need to return an array of object not an object with objects as properties
                    resultsArr.push(result[id]);
                };

                // Patterns should define the columns so when a selection is removed they
                // still respect columns and dont spread out
                jQuery.each(pattern, columnDefine);

                return resultsArr;
            };

            return (pattern === false) ? [selections] : mapResult();
        },
        /**
         * For special sorting
         *
         * @type {Object}
         */
        special: {
            /**
             * HF Sorting
             *
             * @returns {Array}
             *
             * @private
             */
            HFSort: function () {

                /*  Pattern
                    [
                    ['Home/Home','Draw/Home','Away/Home'],
                    ['Draw/Draw','Home/Draw','Away/Draw'],
                    ['Away/Away','Draw/Away','Home/Away'];
                    ];
                */

                var result = [
                    [],
                    [],
                    [],
                ];

                selections = _.sortBy(selections, sortByFBResult);

                /**
                 * Push selections to column
                 *
                 * @param   {String} key
                 * @param   {Object} selection
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var pushSelectionToColumn = function (key, selection) {
                    result[parseInt((selection.fbResult - 1) / 3, 10)][parseInt((selection.fbResult - 1) % 3, 10)]
                        = selection;
                };

                jQuery.each(selections, pushSelectionToColumn);

                return result;
            },
            /**
             * CS Sorting
             *
             * @returns {Array}
             *
             * @private
             */
            CSSort: function () {

                // H D A
                var result = [
                    [],
                    [],
                    [],
                ];


                /**
                 * Push selections to column
                 *
                 * @param   {String} key
                 * @param   {Object} selection
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var pushSelectionToColumn = function (key, selection) {

                    /**
                     * Get columns based on score difference
                     *
                     * @param   {Integer} scoreDiff
                     *
                     * @returns {Integer}
                     *
                     * @private
                     */
                    var getCol = function (scoreDiff) {

                        var col = 1;

                        var checkHome = function () {
                            if (scoreDiff > 0) {
                                col = 0;
                            }
                        };

                        var checkAway = function () {
                            if (scoreDiff < 0) {
                                col = 2;
                            }
                        };

                        checkHome();
                        checkAway();

                        return col;
                    };

                    result[
                        getCol(parseInt(selection.csHome, 10) - parseInt(selection.csAway, 10))].push(selection);
                };

                jQuery.each(selections, pushSelectionToColumn);

                var sortSelections = function (col, selections) {

                    // Selection fields to order
                    var sorting = ['csHome', 'csHome', 'csAway'];

                    result[col] = _.sortBy(
                        selections,
                        function (selection) {

                            return selection[sorting[col]];
                        }
                    );
                    //result[col].reverse();
                };

                jQuery.each(result, sortSelections);

                return result;
            },
            /**
             * Total Goals Sorting
             *
             * @returns {Array}
             *
             * @private
             */
            TGSort: function () {

                orderSelectionsByFBResult(selections);
                return [selections];
            },
            /**
             * To Win/-- Sorting
             *
             * @returns {Array}
             *
             * @private
             */
            TOWINSort: function () {

                orderSelectionsByPrice(selections);
                return [selections];
            },
            /**
             * WM Sorting
             *
             * @returns {Array}
             *
             * @private
             */
            WMSort: function () {

                orderSelectionsByPrice(selections);
                return [selections];
            },
        },
    };

    var sortCode = marketData.marketSort.replace(/^--$/gi, 'TOWIN');
    var isSpecial = (jQuery.inArray(sortCode, ['CS', 'HF', 'TG', 'TOWIN', 'WM']) >= 0);

    return (isSpecial) ? sorting.special[sortCode + 'Sort']() : sorting.genericSort();
};

/**
 * Is Active
 *
 * @param {Object} selectionView
 *
 * @returns {Boolean}
 *
 * @private
 */
var isActive = function (selectionView) {

    /**
     * A set of active checks
     *
     * @type {Object}
     */
    var activeChecks = {

        /**
         * Checks element's parents for disabled market
         *
         * @returns {Boolean}
         *
         * @private
         */
        isActiveParentsMarket: function () {

            return (!selectionView.parents('.disabled-market').length);
        },

        /**
         * Checks element's parents for disabled event
         *
         * @returns {Boolean}
         *
         * @private
         */
        isActiveParentsEvent: function () {

            return (!selectionView.parents('.disabled-event').length);
        },

        /**
         * Checks global elements for disabled market
         *
         * @returns {Boolean}
         *
         * @private
         */
        isActiveParentMarket: function () {

            return (!jQuery('#' + selectionView.data('market')).hasClass('disabled-market'));
        },

        /**
         * Checks global elements for disabled event
         *
         * @returns {Boolean}
         *
         * @private
         */
        isActiveParentEvent: function () {

            return (!jQuery('#' + selectionView.data('event')).hasClass('disabled-event'));
        },

        /**
         * Checks element for disabled class
         *
         * @returns {Boolean}
         *
         * @private
         */
        isActiveSelectionView: function () {

            return (!selectionView.hasClass('disabled'));
        },
    };

    if (activeChecks.isActiveSelectionView() &&
        activeChecks.isActiveParentMarket() &&
        activeChecks.isActiveParentEvent() &&
        activeChecks.isActiveParentsMarket() &&
        activeChecks.isActiveParentsEvent()) {
        return true;
    } else {
        return false;
    }
};

/**
 * Collects tracking information for bets placed.
 * Hompage only. See: https://jira.willhillatlas.com/browse/TDRD-1613
 *
 * @param   {String} selectionView - jQuery element(s) for bet selection.
 *
 * @returns {String} Tracking Information
 *
 * @private
 */
const getTrackingForSelection = (selectionView) => {
    if (window && window.WH && window.WH.sportsbook && window.WH.sportsbook.page === 'home') {
        return selectionView && selectionView.last().closest('[data-panel]').data('panel');
    }
};

/**
 * Run actions for bindEvents
 *
 * @type {Object}
 */
var bindEventsRunActions = {

    /**
     * Handle interim animation before betslip is loaded
     *
     * @param {Object} selectionView
     *
     * @returns {void}
     */
    handleLoadingAnimation: function handleLoadingAnimation(selectionView) {

        selectionView.addClass('betbutton--loading');
    },

    /**
     * Handle animations
     *
     * @param   {Object} selectionView
     *
     * @returns {void}
     *
     * @private
     */
    handleAnimation: function (selectionView) {

        /**
         * Handle animation state
         *
         * @param   {Boolean} isActive
         *
         * @returns {void}
         *
         * @private
         */
        var handleAnimationState = function (selectionView, isActive) {

            var animationClass = isActive ? '-add' : '-remove';

            if (isActive) {
                selectionView.addClass('active');
            } else {
                selectionView.removeClass('active');
            }

            selectionView.removeClass('-selectionadded');

            selectionView.addClass(animationClass);

            setTimeout(function () {

                selectionView.removeClass(animationClass);
                selectionView.toggleClass('-selectionadded', isActive);

            }, 500);


        };

        var selectionActive = selectionView.hasClass('active');

        selectionView.removeClass('betbutton--loading');

        handleAnimationState(selectionView, !selectionActive);
    },

    /**
     * Add to BetSlip action, based on selection model.
     *
     * @param {Object} selectionModel Selection model.
     * @param {Boolean} add Flag to mark whether to Add or Remove the leg.
     * @param {String} selector The selector for the element from which the selection was added/removed
     *
     * @returns {void}
     *
     * @private
     */
    sendToBetSlip: function (selectionModel, add, selector) {

        if (add) {
            BetSlipAdapter.addToBetSlip(selectionModel);
            bindEventsRunActions.publishSelectionAdded(selector);
        } else {
            BetSlipAdapter.removeFromBetSlip(selectionModel);
        }

        manageHighlightModel(selectionModel.id, add);
    },

    /**
     * Publish selection added.
     *
     * @param {String} selector The selector for the element from which the selection was added/removed
     *
     * @returns {void}
     *
     * @private
     */
    publishSelectionAdded: function publishSelectionAdded(selector) {

        if (selector) {

            window.WH.messageBus.publish(MessageBusTopics.BETSLIP_SELECTION_ADDED, {
                addedFrom: selector,
            });
        }
    },

    /**
     * Returns a basic model associated with a selection view.
     *
     * @param {Object} selectionView
     *
     * @returns {Object}
     *
     * @private
     */
    modelFromView: function (selectionView) {

        var element = selectionView.get(0);
        trackLink(element);

        return {
            id: element.getAttribute('data-entityid'),
            name: element.getAttribute('data-name'),
            player: element.getAttribute('data-player'),
            priceNumerator: element.getAttribute('data-num'),
            priceDenominator: element.getAttribute('data-denom'),
            eventId: element.getAttribute('data-event'),
            marketId: element.getAttribute('data-market'),
            displayed: element.getAttribute('data-displayed'),
            status: element.getAttribute('data-status'),
            odds: element.getAttribute('data-odds'),
            analytics: {
                BetListName: [element.getAttribute('data-bet-list-name')],
                BetListPosition: [element.getAttribute('data-bet-list-position')],
                FeatureDescription: [element.getAttribute('data-feature-description')],
            },
            tracking: getTrackingForSelection(selectionView),
        };
    },

    /**
     * Setups and runs the whole thing
     *
     * @param {Object} selectionView
     * @param {String} selector The selector for the element from which the selection was added/removed
     *
     * @returns {void}
     *
     * @private
     */
    init: function (selectionView, selector) {

        /**
         * Case when betslip is not loaded
         *
         * @returns {void}
         */
        var queueAction = function queueAction() {

            if (initialBetslipLoaded) {

                return;
            }

            bindEventsRunActions.handleLoadingAnimation(selectionView);

            betslipClickQueue.push(selectionView);
        };

        /**
         * Case when betslip is loaded
         *
         * @returns {void}
         */
        var initAction = function initAction() {

            if (!initialBetslipLoaded) {

                return;
            }

            bindEventsRunActions.handleAnimation(selectionView);

            bindEventsRunActions.sendToBetSlip(
                bindEventsRunActions.modelFromView(selectionView),
                selectionView.hasClass('active'),
                selector
            );
        };

        if (!selectionView.hasClass('active') &&
            getHighlightedSelectionsCount() >= maxBetslipSelections) {
            return false;
        }

        queueAction();
        initAction();
    },
};

/**
 * This method will take care of handling the quick single on native app
 *
 * @param {Object} selectionView
 *
 * @returns {void}
 *
 * @private
 */
var nativeAppBetHandler = function nativeAppBetHandler(selectionView) {

    var selectionModel = bindEventsRunActions.modelFromView(jQuery(selectionView));
    var leg = BetSlipAdapter.composeLeg(selectionModel);

    window.WH.messageBus.publish(MessageBusTopics.NATIVE_APP_BRIDGE_SEND_MESSAGE, {
        operation: 'quickSingleOpenend',
        data: leg,
    });

};

/**
 * Uses event delegation for binding events to selection views
 *
 * @param {Object} parentView
 * @param {Boolean} duplicateSelections We can have same market more then once on a page, like on competitions.
 * @param {String} selector The selector for the newly loaded selections container
 *
 * @returns {void}
 *
 * @private
 */
var bindEvents = function (parentView, duplicateSelections, selector) {

    /**
     * We can have same market more then once on a page, like on competitions.
     *
     * @param {Object} selectionButton The selection button that was clicked.
     *
     * @returns {Object} the selection view
     *
     * @private
     */
    var getSelectionView = function getSelectionView(selectionButton) {

        var result,
            id = getSelectionId(selectionButton);

        if (duplicateSelections) {

            result = jQuery('button[id=\'' + id + '\']');
        } else {

            var $wrapper = selectionButton.closest('.btmarket__wrapper, .markettable__event');
            result = getSelectionViewFromWrapper($wrapper, id);
        }
        return result;
    };

    /**
     * Click handler to bind to elements.
     *
     * @returns {Boolean} False is is not active
     *
     * @private
     */
    var clickEventHandler = function () {

        var selectionView = getSelectionView(jQuery(this));

        if (!isActive(selectionView)) {

            return false;
        }

        bindEventsRunActions.init(selectionView, selector);
    };


    var buttonSelector = 'button:not(.show-more-button, .banners-carousel__link)';

    // In case we are in a device with touch capabilities:
    if (isTouchDevice()) {

        QuickBets.bindQuickBet({
            parentView: parentView,
            selector: buttonSelector,
            elegibleSelector: 'active',
            placeBetMethod: clickEventHandler,
            nativeAppBetHandler: nativeAppBetHandler,
            onPress: function () {
                //example
                //jQuery(this).addClass('longPressing').css('background-color', 'red');
            },
            onRelease: function (e) {

                e.stopPropagation();
                e.preventDefault();
                //example
                //jQuery(this).removeClass('longPressing').css('background-color', 'blue');
            },
        });

    } else {

        // Rewriting the onclick for each selection to make sure the event or market is not disabled.
        // This will help us to disable an entire event or market just by adding a class to only one element :)
        parentView.off('click.SelectionViewAdapterBindEvents')
            .on('click.SelectionViewAdapterBindEvents', buttonSelector, clickEventHandler);

    }

};

/**
 * Gets the selection id from the entity id data property in case selection id is not defined.
 *
 * @param {Object} selectionButton The selection button that was clicked.
 *
 * @returns {String} id of the selection
 *
 * @private
 */
var getSelectionId = function getSelectionId(selectionButton) {

    return selectionButton.attr('id') || selectionButton.attr('data-entityid');
};

/**
 * Gets the selection View from the wrapper depending if the template has been migrated or not
 *
 * @param {Object} wrapper jQuery object or null if the template is not migrated
 * @param {String} id Selection Id
 *
 * @returns {Object} jquery object
 *
 * @private
 */
var getSelectionViewFromWrapper = function (wrapper, id) {

    if (wrapper && wrapper.length > 0) {

        return wrapper.find('button[id=\'' + id + '\']');
    }

    return getScoreCastOrNormalSelection(id);
};

/**
 * Gets the selection view from either scorecast market or 'regular' market
 *
 * @param {String} id Selection ID
 *
 * @returns {Object} jQuery Object
 *
 * @private
 */
var getScoreCastOrNormalSelection = function (id) {

    var entityIdElements = jQuery('.oddsbutton[data-entityid="' + id + '"]:not([id="' + id + '_SP"])');

    return (entityIdElements.length) ? entityIdElements : jQuery('#' + id);

};

/**
 * Function tries to detect if current device has touch capabilities, at first it's checking for 'wh_device'
 * cookie and see if its desktop OS or not and then it checks fot 'ontouchstart' in document, although
 * there is no 100% reliable way of detecting the touch capabilities.
 *
 * @returns {Boolean}
 *
 * @private
 */
var isTouchDevice = function () {

    var whDevice = getWhDevice();

    if (whDevice && whDevice.device_os) { // jshint ignore: line

        return whDevice.device_os !== 'desktop' && 'ontouchstart' in document; // jshint ignore: line
    } else {

        return 'ontouchstart' in document;
    }
};

/**
 * Function looks for 'wh_device' cookie and parses it's value
 *
 * @returns {Object}
 *
 * @private
 */
var getWhDevice = function () {

    var whDeviceCookie = docCookies.getItem('wh_device');

    if (whDeviceCookie) {

        return parseToJson(whDeviceCookie);
    }

    return null;
};

/**
 * Function parses value JSON string, if its no a valid JSON string, it returns null
 *
 * @param {String} value A JSON string
 *
 * @returns {Object}
 *
 * @private
 */
var parseToJson = function (value) {

    var json = null;

    try {

        json = JSON.parse(value);
    } catch (e) {

        json = null;
    }

    return json;
};

/**
 * Callback on messageBus message with bet IDs as data
 *
 * @param {String} eventName Name of the even published
 * @param {Array} selections An array of selection IDs
 *
 * @returns {void}
 *
 * @private
 */
var highlightedSelectionsCallback = function (eventName, selections) {

    _.each(selections, function (selectionId) {

        if (selectionId) {

            onBetSlipUpdate(selectionId, true);
        }
    });
};

/**
 * Returns button text
 *
 * @param {String} key key of window.WH dict for translation value
 * @param {String} defaultValue Text button when translation is not found
 *
 * @returns {String}
 *
 * @private
 */
const handleButtonsTranslations = (key, defaultValue) => {
    if (Translator.hasTranslation(key)) {

        return Translator.translate(key);
    }
    return defaultValue;
};

/**
 * Market show more selection handler
 *
 * @returns {void}
 *
 * @private
 */
var marketShowMoreHandler = function marketShowMoreHandler() {

    var showMoreButton = jQuery(this);
    var selectionsGrid = showMoreButton.closest('.grid').prevAll('div.btmarket');
    var buttonText;

    if (selectionsGrid.is(':visible')) {
        buttonText = handleButtonsTranslations('competitions.showMore', 'Show more');
        showMoreButton.html(`<span>${buttonText}</span>`).addClass('show-more-down').removeClass('show-more-up');
    } else {
        buttonText = handleButtonsTranslations('competitions.showLess', 'Show less');
        showMoreButton.html(`<span>${buttonText}</span>`).addClass('show-more-up').removeClass('show-more-down');
    }

    selectionsGrid.toggle();
};


/**
 * Binding market show more selections
 *
 * @param {Object} parentView The parent container
 *
 * @returns {void}
 *
 * @private
 */
var bindMarketShowMore = function bindMarketShowMore(parentView) {

    parentView.off('click.BindMarketShowMore')
        .on('click.BindMarketShowMore', '.btmarket__wrapper a.market-show-more',
            marketShowMoreHandler);
};

/**
 * Scroll window view to fit provided market header in view
 *
 * @param {Object} headerDropdown jQuery node
 *
 * @returns {void}
 *
 * @private
 */
var scrollToMarketHeader = function scrollToMarketHeader(headerDropdown) {
    
    if (headerDropdown && headerDropdown.offset()) {
        var headerDropdownOffset = headerDropdown.offset().top;
        var mobileHeaderOffset = jQuery('#header').height();

        if (jQuery(window).scrollTop() + mobileHeaderOffset > headerDropdownOffset) {

            jQuery(window).scrollTop(headerDropdownOffset - mobileHeaderOffset);
        }
    }
};

/**
 * Showing one label and hide second one
 *
 * @param {Object} labelToShow The jquery element to show
 * @param {Object} labelToHide The jquery element to hide
 *
 * @returns {void}
 *
 * @private
 */
var showAndHideLabel = function showAndHideLabel(labelToShow, labelToHide) {

    labelToShow.show();
    labelToHide.hide();
};

/**
 * Selection show more selection handler
 *
 * @returns {void}
 *
 * @private
 */
var selectionShowMoreHandler = function selectionShowMoreHandler() {

    var showMoreButton = jQuery(this);
    var selectionsGrid = showMoreButton.closest('.event').find('div.selection-container').last();

    if (selectionsGrid.hasClass('hide-selections')) {

        showAndHideLabel(showMoreButton.find('.selection-show-less-label'),
            showMoreButton.find('.selection-show-more-label'));
    } else {

        showAndHideLabel(showMoreButton.find('.selection-show-more-label'),
            showMoreButton.find('.selection-show-less-label'));

        scrollToMarketHeader(
            showMoreButton.closest('div.event')
        );
    }

    selectionsGrid.toggleClass('hide-selections');
};

/**
 * Binding selection show more selections
 *
 * @param {Object} parentView The parent container
 *
 * @returns {void}
 *
 * @private
 */
var bindSelectionsShowMore = function bindSelectionsShowMore(parentView) {

    parentView.off('click.BindSelectionsShowMore')
        .on('click.BindSelectionsShowMore', 'a.selection-show-more:not(.event-show-more)',
            selectionShowMoreHandler);

    bindMarketShowMore(parentView);
};

/**
 * Initialises the extra adaptors when we init this adaptor
 *
 * @returns {void}
 *
 * @private
 */
var initExtras = function initExtras() {

    BetDropdownViewAdapter.init();
};

/**
 * Performs the event bindings
 *
 * @return {void}
 *
 * @private
 */
var doEventBindings = function doEventBindings() {

    var clickableSelections = jQuery('.clickable-selections');

    // format the date entries
    formatDateEntries(clickableSelections);
    //make sure the last parameter is true only for competitions
    bindEvents(jQuery('.banners-container'));
    bindEvents(clickableSelections, UrlArgsProvider.get().page === 'competitions');
    bindSelectionsShowMore(clickableSelections);
};

/**
 * Render the date as an element's text based on its datetime attribute
 *
 * @returns {void}
 *
 * @private
 */
var renderDate = function () {

    var startDate = jQuery(this).attr('datetime');
    jQuery(this).text(Date.format(startDate, 'DD MMM HH:mm'));
};

/**
 * Finds datetime elements and sends them to have their text value rendered
 *
 * @param  {Object} content The clickable selections areas of the page as a jQuery node
 *
 * @return {void}
 *
 * @private
 */
var formatDateEntries = function formatDateEntries(content) {

    content
        .find('section.event header time, time.eventStartTime')
        .each(renderDate);
};

/**
 * Binds the events on selection buttons inside the container determined by the given selector
 *
 * @param  {String} topic the bus message topic
 * @param {String} selector The selector for the newly loaded selections container
 *
 * @return {void}
 *
 * @private
 */
var loadSelectionsHandler = function loadSelectionsHandler(topic, selector) {

    var loadedSelectionsContainer = jQuery(selector);
    formatDateEntries(loadedSelectionsContainer);
    bindEvents(loadedSelectionsContainer, null, selector);
};

/**
 * Methods to be public
 *
 * @type {Object}
 */
var api = {

    /**
     * Clear all selections
     *
     * @returns {void}
     *
     * @public
     */
    clearSelections: function () {

        highlightedSelections = [];
        jQuery('.clickable-selections .active').removeClass('active');
        jQuery('.banners-container .active').removeClass('active');
        BetSlipAdapter.displayCounts();
    },

    /**
     * Exposes the sortInColumns method through the api
     *
     * @type {Function}
     *
     * @public
     */
    sortInColumns: sortInColumns,

    /**
     * Init function, should be called once the dom is ready.
     *
     * @returns {void}
     *
     * @public
     */
    init: function () {

        initExtras();
        api.highlightAddedSelections();
        doEventBindings();
    },

    /**
     * Subscribes to BetslipAdapter.getBets and initiates obtaining selections from betslip
     *
     * @returns {void}
     *
     * @public
     */
    highlightAddedSelections: function () {

        window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_ADAPTER_GET_BETS, highlightedSelectionsCallback);
        BetSlipAdapter.getSelectionsFromBetslip(0);
    },

    /**
     * Array of selection IDs added to betslip
     *
     * @type {Array}
     */
    highlightedSelections: highlightedSelections,
};

// Subscribe to BetSlip update event
BetSlipAdapter.onUpdate.push(onBetSlipUpdate);
//this component can process selections loaded by other components , like the betslip
window.WH.messageBus.subscribe(
    MessageBusTopics.SELECTION_VIEW_ADAPTER_SELECTIONS_LOADED, function (topic, selector) {

        modelComposer.generateMergedModel(window.WH.sportsbook.model);
        loadSelectionsHandler(topic, selector);
    }
);

// Subscribe to initial betslip event emitting selection in betslip
window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_ADAPTER_GET_BETS, initialBetsSubscribe);

export default api;
